import { HttpClient } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SalesDocument, SalesDocumentPosition } from '@icc/b2b/shared';
import { ConfigService } from '@icc/shared/config.service';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { TranslateService } from '@ngx-translate/core';

interface PrintTemplate {
    id: number;
    name: string;
    description: string;
    hasPositionList: boolean;
    hasSummary: boolean;
    hasSketchList: boolean;
    hasMontageSchemeList: boolean;
}

interface PositionTemplate {
    id: number;
    name: string;
    description: string;
    hasSketchList: boolean;
    hasMontageSchemeList: boolean;
}

interface Division {
    kind: 'category' | 'department' | 'configurator';
    name: string;
    description: string;
    id: number | string;
    positionsIds: string[];
    templates: PositionTemplate[];
    templatesIds: number[];
}

@Component({
    selector: 'icc-print-templated-dialog',
    templateUrl: './print-templated-dialog.component.html',
    styleUrls: ['./print-templated-dialog.component.scss'],
})
export class PrintTemplatedDialogComponent {
    divisions: Division[] = [];

    printTemplates: PrintTemplate[] = [];

    selectedDocumentId: string;
    selectedTemplatesIds: Record<string, number> = {};
    selectedTemplates: Record<string, PositionTemplate> = {};
    selectedMainTemplateId: number;
    selectedMainTemplate: PrintTemplate;
    selectedMainTemplateDescription: SafeHtml;

    type: 'standard' | 'templated' = 'standard';
    printSettings = {
        printSummary: true,
        printSketches: true,
        printMontageSchemas: true,
        printAttachments: true,
    };

    showSketchesSettings = false;
    showMontageSchemasSettings = false;

    constructor(
        @Inject(DIALOG_DATA)
        public data: {
            mode: 'print' | 'download';
            document: SalesDocument;
            positions: SalesDocumentPosition[];
            buyPositionsPerDocument: Record<string, SalesDocumentPosition[]>;
            selectBuyDocument: boolean;
            buyDocuments: SalesDocument[];
            departments: Record<number, { name: string; priority: number } | string>;
            sellerCompanyName: string;
            hasAttachments: boolean;
        },
        private translateService: TranslateService,
        private dialogRef: DialogRef,
        private configService: ConfigService,
        private http: HttpClient,
        private domSanitizer: DomSanitizer
    ) {
        this.init();
    }

    async init() {
        if (this.data.selectBuyDocument) {
            this.selectedDocumentId = this.data.buyDocuments[0].uuid;
        } else {
            this.selectedDocumentId = this.data.document.uuid;
        }
        this.getDivisions();
    }

    async getDivisions() {
        const { divisions, printTemplates } = await this.http
            .get<{
                divisions: Division[];
                printTemplates: PrintTemplate[];
            }>('/api/pdf/templates-settings/' + this.selectedDocumentId)
            .toPromise();
        this.divisions = divisions;
        this.printTemplates = printTemplates;
        if (this.printTemplates.length > 0) {
            if (
                !this.selectedMainTemplateId ||
                !this.printTemplates.some((t) => +t.id === +this.selectedMainTemplateId)
            ) {
                this.onMainTemplateChange(printTemplates[0].id);
            }
        }

        for (const division of this.divisions) {
            if (division.templatesIds.length > 0) {
                if (
                    !this.selectedTemplatesIds[division.kind + '-' + division.id] ||
                    !division.templates.some(
                        (t) => +t.id === +this.selectedTemplatesIds[division.kind + '-' + division.id]
                    )
                ) {
                    this.onDivisionTemplateChange(division, division.templatesIds[0]);
                }
            }
        }
    }

    close() {
        this.dialogRef.close();
    }

    print() {
        this.dialogRef.close({
            documentId: this.selectedDocumentId,
            type: this.type,
            selectedTemplates: this.selectedTemplatesIds,
            selectedMainTemplate: this.selectedMainTemplateId,
            printSettings: this.printSettings,
        });
    }

    onDocumentChange(documentId: string) {
        this.selectedDocumentId = documentId;
        this.getDivisions();
    }

    onMainTemplateChange(templateId: number) {
        this.selectedMainTemplateId = templateId;
        this.selectedMainTemplate = this.printTemplates.find((t) => t.id === +templateId);
        this.selectedMainTemplateDescription = this.selectedMainTemplate?.description
            ? this.domSanitizer.bypassSecurityTrustHtml(this.selectedMainTemplate.description)
            : null;

        this.showSketchesSettings = this.hasSketches();
        this.showMontageSchemasSettings = this.hasMontageSchemas();
    }

    onDivisionTemplateChange(division: Division, templateId: number) {
        this.selectedTemplatesIds[division.kind + '-' + division.id] = +templateId;
        this.selectedTemplates[division.kind + '-' + division.id] = division.templates.find(
            (t) => t.id === +templateId
        );

        this.showSketchesSettings = this.hasSketches();
        this.showMontageSchemasSettings = this.hasMontageSchemas();
    }

    hasSketches() {
        return (
            this.selectedMainTemplate?.hasSketchList ||
            (this.selectedMainTemplate?.hasPositionList &&
                this.divisions.some(
                    (d) => this.selectedTemplates[d.kind + '-' + d.id]?.hasSketchList
                ))
        );
    }

    hasMontageSchemas() {
        return (
            this.selectedMainTemplate?.hasMontageSchemeList ||
            (this.selectedMainTemplate?.hasPositionList &&
                this.divisions.some(
                    (d) => this.selectedTemplates[d.kind + '-' + d.id]?.hasMontageSchemeList
                ))
        );
    }

    getDepartmentName(departmentId: number): string {
        const department = this.data.departments?.[departmentId];
        if (!department) return '';
        return typeof department === 'string' ? department : department.name;
    }

    trackById(index: number, item: any) {
        return item.id;
    }
}
